import CssBaseline from '@material-ui/core/CssBaseline'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { ThemeProvider as ThemeProviderMuiV5 } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { LicenseInfo } from '@mui/x-license-pro'
import * as Sentry from '@sentry/browser'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import CustomParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import isoWeek from 'dayjs/plugin/isoWeek'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import objectSupport from 'dayjs/plugin/objectSupport'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import i18n from 'i18next'
import Backend from 'i18next-xhr-backend'
import React from 'react'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - FIXME (tchow 2025/03/12): Remove current comment when upgrading to react 19
import { createRoot } from 'react-dom/client'
import { initReactI18next } from 'react-i18next'
import { BrowserRouter } from 'react-router-dom'
import urljoin from 'url-join'
import 'chart.js/auto'

import packageJson from 'package.json'

import { loadGoogleAnalytics } from './commons/analytics'
import ScrollToTop from './components/ScrollToTop'
import OnboardingJoyrideWrapper from './Joyride/OnboardingJoyride/OnBoardingJoyrideWrapper'
import App from './layout'
import ReleaseJoyride from './ReleaseAssets/ReleaseJoyride'
import './index.css'
import SETTINGS from './settings'
import theme, { themeMuiV5 } from './theme'
import 'node_modules/react-image-gallery/styles/css/image-gallery.css'
// Custom CSS for Image Gallery
import './styles/ImageGallery.style.css'

// React-Grid-Layout
import 'node_modules/react-grid-layout/css/styles.css'
import 'node_modules/react-resizable/css/styles.css'

import 'dayjs/locale/en'
import 'dayjs/locale/fr'
import 'dayjs/locale/de'

const VALID_LANGUAGES = ['en', 'en-gb', 'fr', 'de']

const language = getValidLanguage(getBrowserLanguage(), VALID_LANGUAGES, SETTINGS.app.language)

function getBrowserLanguage() {
  // navigator.userLanguage for IE
  return navigator.languages ? navigator.languages[0] : navigator.language || (navigator as any).userLanguage
}

export function getValidLanguage(language: string, validLanguages: string[], fallback: string) {
  if (validLanguages.some((element) => language.indexOf(element) != -1)) {
    return language
  }
  return fallback
}

export function configure() {
  if (SETTINGS.app.sentry_dsn) {
    Sentry.init({
      dsn: SETTINGS.app.sentry_dsn,
      debug: SETTINGS.debug,
      release: packageJson.version,
      environment: SETTINGS.app.brand_id,
    })
  }

  i18n
    .use(Backend)
    .use(initReactI18next) // passes i18n down to react-i18next
    .init({
      debug: process.env.NODE_ENV !== 'production',
      fallbackLng: SETTINGS.app.language,
      lng: language,
      load: 'languageOnly',

      backend: {
        // Required because the frontend might not be exposed at the domain root
        loadPath: urljoin(SETTINGS.basePath, '/locales/{{lng}}/{{ns}}.json'),
        // Append the git hash so that the URL is saw as new on each release
        queryStringParams: { v: packageJson.version },
      },

      // allow keys to be phrases having `:`, `.`
      nsSeparator: false,
      keySeparator: false,

      // do not consider null or "" as translations
      returnNull: false,
      returnEmptyString: false,

      interpolation: {
        escapeValue: false,
      },
      react: {
        wait: true, // Wait for namespace to be loaded before displaying child component
        // see https://github.com/i18next/react-i18next/issues/796
        // and https://github.com/i18next/react-i18next/issues/1124
        // we don't need the binding as we never change the language dynamically, also
        // it has bugs with React 16.13 when trying to update the state. This both improves
        // performance, and enables us to change the language on-demand without console errors.
        bindI18n: false,
        useSuspense: false,
      },
    })

  // Change dayjs local globally
  dayjs.locale(language)
  dayjs.extend(utc)
  dayjs.extend(timezone)
  dayjs.extend(LocalizedFormat)
  dayjs.extend(duration)
  dayjs.extend(relativeTime)
  dayjs.extend(CustomParseFormat)
  dayjs.extend(isSameOrBefore)
  dayjs.extend(isoWeek)
  dayjs.extend(objectSupport)
  dayjs.extend(advancedFormat)
}

function setupMuiLicense() {
  LicenseInfo.setLicenseKey(SETTINGS.app.mui_license_key)
}

export async function bootstrapApp(rootElementId: string) {
  if (SETTINGS.app.google_analytics_id) {
    await loadGoogleAnalytics(SETTINGS.app.google_analytics_id, SETTINGS.app.google_analytics_cookie_flags)
  }

  setupMuiLicense()
  const container = document.getElementById(rootElementId) as HTMLElement
  const root = createRoot(container)
  root.render(
    <React.Fragment>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <MuiThemeProvider theme={theme}>
          <ThemeProviderMuiV5 theme={themeMuiV5}>
            <BrowserRouter>
              <OnboardingJoyrideWrapper steps={ReleaseJoyride} autostartLocation="startTour">
                <ScrollToTop />
                <App />
              </OnboardingJoyrideWrapper>
            </BrowserRouter>
          </ThemeProviderMuiV5>
        </MuiThemeProvider>
      </LocalizationProvider>
    </React.Fragment>
  )
}
